<template>
	<!-- Large Nav -->
	<nav v-if="width > 992" class="navbar navbar-expand-lg bg-body-tertiary">
		<div class="container-fluid px-0 align-items-center justify-content-between">
			<p class="w-25 fw-semi-bold fs-4 m-0 navbar-brand">ExploreMeds</p>

			<p v-if="!consented && selectedSurveyType" class="m-0 fs-4 fw-medium w-50 text-center">Survey Consent</p>
			<p v-if="!consented && !selectedSurveyType" class="m-0 fs-4 fw-medium w-50 text-center">Choose Survey
				Type</p>

			<div v-if="!consented" class="w-25 d-flex justify-content-end">
				<button v-load-page="'/'" v-ripple
				        class="btn btn-white border-0 rounded-pill bg-hover-gray lg-sv-btn" type="button">Exit
				</button>
			</div>

			<div v-if="consented" class="w-25 d-flex justify-content-end">
				<button v-ripple class="btn btn-white border-0 rounded-pill bg-hover-gray lg-sv-btn"
				        data-bs-target="#saveModal"
				        data-bs-toggle="modal" type="button">Save and Exit
				</button>
			</div>
		</div>
	</nav>

	<!-- Small Nav -->
	<nav v-else class="navbar small-nav p-0">
		<div class="container-fluid justify-content-between p-0 flex-nowrap">
			<i v-if="consented && surveyFound" v-ripple class="hoverable-icon bi-arrow-left" @click="onBack"></i>

			<p v-if="!consented && selectedSurveyType" v-max-lines="1" class="m-0 fs-5 fw-medium line-break-any">Survey
				Consent</p>
			<p v-if="!consented && !selectedSurveyType" v-max-lines="1" class="m-0 fs-5 fw-medium line-break-any">Choose
				Survey Type</p>

			<i v-if="!consented" v-load-page="'/'" v-ripple class="hoverable-icon bi-x"></i>
			<i v-if="consented" v-ripple class="hoverable-icon bi-x" data-bs-target="#saveModal"
			   data-bs-toggle="modal"></i>
		</div>
	</nav>

	<!-- No Survey Found -->
	<div v-if="!surveyFound" class="row">
		<div class="container-fluid px-0">
			<div class="container-fluid px-0 text-center">
				<h3>No such Survey Exists!</h3>
				<p class="text-secondary mb-4">The survey you are looking for does not exist or has been deleted.</p>
				<p>Please click the URL below or navigate back to the home page.</p>
				<RouterLink to="/">Go back to the Explore Meds homepage</RouterLink>
			</div>
		</div>
	</div>

	<!-- Progress Bar -->
	<div v-if="consented && surveyFound" class="progress thin">
		<div :aria-valuenow="Math.round(progressPercent)" :style="{ width: progressPercent + '%' }"
		     aria-label="Survey Progress"
		     aria-valuemax="100"
		     aria-valuemin="0" class="progress-bar bg-secondary" role="progressbar">
		</div>
	</div>

	<!-- Survey Questions Area -->
	<div v-if="consented && surveyFound && currentQuestion" ref="surveyQuestionsArea" class="row">
		<div :class="width < 500 ? 'overflow-hidden' : ''" class="w-100 w-md-50 mw-500 mx-auto pb-lg">
			<Transition :name="transitionName" mode="out-in">
				<component :is="componentType"
				           :key="currentQuestionIndex"
				           :answer="currentQuestion.answer"
				           :data="currentQuestionData"
				           :question-description="safeQuestionHtml"
				           :question-sub-text="safeSubTextHtml"
				           @question-answered="onAnswered"/>
			</Transition>
		</div>

		<div class="w-100 w-md-50 mw-500 mx-auto fixed-bottom pt-2 pb-4 pb-lg-2r d-flex gap-3 bg-white">
			<button :disabled="!hasPrev" class="btn btn-outline-dark rounded-pill py-7 w-25 d-none d-lg-block"
			        @click="onBack">Back
			</button>

			<button v-if="hasNext" :class="width < 992 ? 'w-100' : 'w-75'"
			        :disabled="!canProceed"
			        class="btn btn-dark rounded-pill py-7 w-sm-100"
			        @click="onForward">
				{{ currentQuestion?.data.skippable && (!currentQuestion?.answer || currentQuestion?.answer.length === 0) ? 'Skip and Continue' : 'Continue'
				}}
			</button>

			<button v-else :class="width < 992 ? 'w-100' : 'w-75'" :disabled="!canProceed"
			        class="btn btn-dark rounded-pill py-7 w-sm-100"
			        data-bs-target="#saveModal" data-bs-toggle="modal">End Survey
			</button>
		</div>
	</div>

	<!-- Consent Area -->
	<div v-if="!consented && surveyFound" ref="consentArea" class="row">
		<div v-if="!selectedSurveyType && !props.id" class="w-100 w-md-50 mw-500 mx-auto pb-lg">
			<BoxChoicesGeneral :data="surveyTypes" :hide-value="true" :show-error="false"
			                   @question-answered="updateSurveyType"/>
		</div>

		<template v-else>
			<div :class="width < 500 ? 'overflow-hidden' : ''" class="w-100 w-md-50 mw-500 mx-auto pb-lg">
				<div class="p-4 p-md-5">
					<div>
						<p class="fs-4 fw-semibold mb-3">Before you begin the survey...</p>
						<p class="text-secondary mb-4">
							We value your privacy and want to ensure you are informed about how we handle your data.
							Please take a moment to read through our consent information below:
						</p>

						<ul class="list-unstyled text-secondary mb-4">
							<li v-if="props.type === 'anonymous' && !props.id" class="mb-3 d-flex">
								<i class="bi-check-circle-fill text-success me-2"></i>
								Your responses will be anonymous and cannot be linked back to you.
							</li>
							<li v-else-if="props.type !== 'anonymous' && !props.id" class="mb-3 d-flex">
								<i class="bi-check-circle-fill text-success me-2"></i>
								Your responses will be associated with your account.
							</li>
							<li v-else-if="props.type !== 'anonymous' && props.id" class="mb-3 d-flex">
								<i class="bi-check-circle-fill text-success me-2"></i>
								You are editing your previous responses. Updated answers will replace your earlier ones.
							</li>
							<li class="mb-3 d-flex"><i class="bi-check-circle-fill text-success me-2"></i> Your
								responses will be kept confidential and used solely for research purposes.
							</li>
							<li class="mb-3 d-flex"><i class="bi-check-circle-fill text-success me-2"></i> We will not
								share your personal information with any third parties without your explicit consent.
							</li>
							<li class="mb-3 d-flex"><i class="bi-check-circle-fill text-success me-2"></i> You have the
								right to withdraw from the survey at any time without any consequences.
							</li>
							<li class="mb-3 d-flex"><i class="bi-check-circle-fill text-success me-2"></i> If you have
								any questions or concerns about the survey or how your data will be used, please reach
								out to us.
							</li>
						</ul>

						<div class="form-check mb-3">
							<input id="consentCheckbox" v-model="consentChecked" class="form-check-input"
							       type="checkbox">
							<label class="form-check-label fw-medium" for="consentCheckbox">
								<span v-if="props.type === 'anonymous' && !props.id">I understand and agree to participate anonymously.</span>
								<span v-else-if="props.type !== 'anonymous' && !props.id">I understand and agree to participate with my account.</span>
								<span v-else-if="props.type !== 'anonymous' && props.id">I confirm that I am updating my previous responses.</span>
							</label>
						</div>
					</div>
					<p class="mb-4">By clicking {{ props.id ? 'Resume Survey' : 'Start Survey' }}, you acknowledge that
						you have read and understood the above information and consent to participate in the survey.</p>
				</div>
			</div>

			<div class="w-100 w-md-50 mw-500 mx-auto fixed-bottom pt-2 pb-4 pb-lg-2r d-flex gap-3 bg-white">
				<button v-if="!props.id" class="btn btn-outline-dark rounded-pill py-7 w-25"
				        @click="selectedSurveyType = null">Back
				</button>
				<button v-else v-load-page="'/profile'" class="btn btn-outline-dark rounded-pill py-7 w-25">Exit
				</button>
				<button v-if="!props.id" :class="width < 992 ? 'w-100' : 'w-75'" :disabled="!consentChecked"
				        class="btn btn-dark rounded-pill py-7 w-sm-100" @click="saveConsentAndCreateSurvey">Start Survey
				</button>
				<button v-else :class="width < 992 ? 'w-100' : 'w-75'" :disabled="!consentChecked"
				        class="btn btn-dark rounded-pill py-7 w-sm-100" @click="fetchSurveyData">Resume Survey
				</button>
			</div>
		</template>
	</div>

	<!-- Save Modal -->
	<div v-if="consented && surveyFound" id="saveModal" aria-hidden="true" aria-labelledby="saveModalLabel"
	     class="modal fade" tabindex="-1">
		<div class="modal-dialog modal-dialog-centered sheetModal popOutModal">
			<div class="modal-content rounded-sm">
				<div class="modal-body d-flex flex-column py-4 align-items-center">
					<p class="fs-4 fw-semibold text-center mb-2">End Survey</p>
					<p v-if="!allQuestionsAnswered" class="text-center mb-2 text-secondary mb-4">Are you sure you want
						to quit?</p>
					<p v-if="props.type === 'identified'" class="text-center mb-3 text-secondary mb-4">Your survey will
						be saved, and you will be able to continue at any time.</p>
					<p v-else class="text-center mb-3 text-secondary mb-4">Your responses will be submitted anonymously,
						and you will not be able to return to this survey.</p>
					<div class="d-flex w-100 gap-3">
						<button v-if="props.id" v-load-page="'/profile'" v-ripple
						        class="btn btn-outline-dark w-25 rounded-pill py-7 mb-sm-3" data-bs-dismiss="modal">
							Exit
						</button>
						<button v-else v-ripple class="btn btn-outline-dark w-25 rounded-pill py-7 mb-sm-3"
						        data-bs-dismiss="modal" @click="removeSurvey">
							Exit
						</button>
						<button v-ripple class="btn btn-outline-dark w-25 rounded-pill py-7 mb-sm-3"
						        data-bs-dismiss="modal">Close
						</button>
						<button v-ripple class="btn btn-dark w-50 rounded-pill py-7 mb-sm-3" data-bs-dismiss="modal"
						        @click="submitSurvey">
							{{ props.type === 'anonymous' || allQuestionsAnswered ? 'Submit and Exit' : 'Save and Exit'
							}}
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useWindowSize } from '@vueuse/core'
import CheckboxChoice from '@/components/Survey/CheckboxChoice.vue';
import LikertScale from '@/components/Survey/LikertScale.vue';
import RadioChoice from '@/components/Survey/RadioChoice.vue';
import SliderInput from '@/components/Survey/SliderInput.vue'
import BoxChoices from '@/components/Survey/BoxChoices.vue';
import DropdownSelect from '@/components/Survey/DropdownSelect.vue';
import MonthPicker from '@/components/Survey/MonthPicker.vue';
import DatePicker from '@/components/Survey/DatePicker.vue';
import apiClient from "@/utils/axios.js";
import { useAppHelpers } from "@/composables/useAppHelpers.js";
import { useHttpErrorManager } from "@/composables/useHttpErrorManager.js";
import { useAuthStore } from "@/stores/auth-store.js";
import { useRoute, useRouter } from "vue-router";
import BoxChoicesGeneral from "@/components/BoxChoicesGeneral.vue";
import { useToast } from "@/composables/useToast.js";
import { useActiveTimer } from "@/composables/useActiveTimer";

const props = defineProps({
	type: {type: String, default: 'anonymous'}, // 'anonymous' or 'identified'
	id: {type: [String, Number], default: null}
});

const helper = useAppHelpers();
const auth = useAuthStore();
const router = useRouter();
const route = useRoute()

const {width} = useWindowSize();
const toast = useToast();
const {seconds, start} = useActiveTimer(30000);
const transitionName = ref('');

const currentQuestionIndex = ref(0);
const currentQuestion = ref(null);
const questions = ref([]);
const questionVariablesValues = ref({});
const validVariables = ref([]);

const surveyQuestionsArea = ref(null);
const surveyId = ref(props.id);
const surveyFound = ref(true);
const survey = ref(null);

const consentArea = ref(null);
const consented = ref(false);
const consentChecked = ref(false);

const expectedTotalSurveyTime = ref(0);
const selectedSurveyType = ref(null);
const surveyTypes = ref({
	options: [
		{iconClass: 'bi-capsule-pill', label: 'Medication I am Taking', description: null, value: 1},
		{iconClass: 'bi-prescription2', label: 'Medication that I have Taken', description: null, value: 2}
	]
});

// Safe text helpers
const escapeHtml = (s = "") => String(s)
.replace(/&/g, "&amp;")
.replace(/</g, "&lt;")
.replace(/>/g, "&gt;")
.replace(/"/g, "&quot;")
.replace(/'/g, "&#39;");

// Detect starred placeholders [* var *]
const hasStarredPlaceholder = (text) => {
	if(!text) {
		return false;
	}
	const regex = /\[\*\s*([a-zA-Z0-9_ ]+)\s*\*\]/g;
	let match;
	while((match = regex.exec(text)) !== null) {
		const variable = match[1].trim();
		if(validVariables.value.includes(variable)) {
			return true;
		}
	}
	return false;
}

// Return first starred placeholder name without stars
const getStarredPlaceholderName = (text) => {
	if(!text) {
		return null;
	}
	const regex = /\[\*\s*([a-zA-Z0-9_ ]+?)\s*\*\]/;
	const match = regex.exec(text);
	if(match) {
		return match[1].trim();
	}
	return null;
}

// Build safe HTML by escaping base text and only injecting underlined escaped values for placeholders
const renderWithPlaceholders = (text) => {
	if(!text) {
		return "";
	}

	return text.replace(/\[(\*?)([a-zA-Z0-9_ ]+?)(\*?)\]/g, (match, start, variable, end) => {
		const cleanVariable = variable.trim();

		if(start === "*" && end === "*") {
			return "";
		}

		if(validVariables.value.includes(cleanVariable)) {
			const variableValue = questionVariablesValues.value[cleanVariable];
			if(Array.isArray(variableValue) && variableValue.length > 0) {
				const joined = variableValue.map(v => escapeHtml(v?.text ?? v?.value ?? "")).filter(Boolean).join(", ");
				return joined ? `<u>${ joined }</u>` : "";
			}

			return "";
		}

		return match;
	});
};

const safeQuestionHtml = computed(() => renderWithPlaceholders(currentQuestion.value?.data?.meta?.question || ""));
const safeSubTextHtml = computed(() => renderWithPlaceholders(currentQuestion.value?.data?.meta?.subText || ""));

const allQuestionsAnswered = computed(() => questions.value.every(q => q.answer && q.answer.length > 0));

const updateSurveyType = (choice) => {
	if(choice) {
		selectedSurveyType.value = choice.value;
	}
}

const saveConsentAndCreateSurvey = async() => {
	helper.loaderOn(consentArea.value, false, false);
	try {
		const response = await apiClient.post('member/surveys/saveConsentAndCreateSurvey', {
			consentGiven: consentChecked.value,
			isAnonymous: props.type === 'anonymous',
			surveyType: selectedSurveyType.value
		});
		if(response.status === 200) {
			survey.value = response.data.data.survey;
			validVariables.value = response.data.data.variables;
			validVariables.value.push('all_side_effects');
			validVariables.value.push('all_symptoms');
			selectedSurveyType.value = survey.value.type;
			structureQuestions(response.data.data.questions);
			setCurrentToFirstVisible();

			surveyFound.value = true;
			consented.value = true;
			surveyId.value = survey.value.uuid;
			start();
			if(auth.isLoggedIn) {
				window.history.replaceState({}, '', '/survey/' + props.type + '/' + surveyId.value);
			}
		}
	}
	catch(error) {
		await useHttpErrorManager().handleError(error, true);
		consented.value = false;
	}
	finally {
		helper.loaderOff(consentArea.value);
	}
}

const fetchSurveyData = async() => {
	if(surveyQuestionsArea.value) {
		helper.loaderOn(surveyQuestionsArea.value, false, false);
	}
	try {
		const response = await apiClient.post('member/surveys/fetchSurveyData', {
			surveyId: props.id,
			consentGiven: consentChecked.value
		});
		if(response.status === 200) {
			consented.value = true;
			survey.value = response.data.data.survey;

			if(survey.value.is_completed === 1) {
				toast.displayToast({heading: 'Survey Already Completed', message: 'You have already completed this survey!'}, 'outline-info', 8000);
				await router.push({path: '/'});
				return;
			}

			validVariables.value = response.data.data.variables;
			validVariables.value.push('all_side_effects');
			validVariables.value.push('all_symptoms');
			selectedSurveyType.value = survey.value.type;
			structureQuestions(response.data.data.questions);

			// Clamp to valid bounds and ensure it's visible; fallback to first visible
			const last = Number(survey.value.last_attempted_qs ?? 0);
			currentQuestionIndex.value = isFinite(last) ? Math.min(Math.max(0, last), Math.max(0, questions.value.length - 1)) : 0;
			if(!hasQuestionConditionsMet(questions.value[currentQuestionIndex.value])) {
				setCurrentToFirstVisible();
			}
			else {
				currentQuestion.value = questions.value[currentQuestionIndex.value];
			}
			assignValuesToVariablesOnLoad();

			surveyFound.value = true;
			surveyId.value = survey.value.uuid;
		}
	}
	catch(error) {
		await useHttpErrorManager().handleError(error, true);
		if(error?.status === 404) {
			surveyFound.value = false;
		}
		throw error;
	}
	finally {
		if(surveyQuestionsArea.value) {
			helper.loaderOff(surveyQuestionsArea.value);
		}
	}
}

const structureQuestions = (questionsArray) => {
	questions.value = [];
	expectedTotalSurveyTime.value = 0;
	questionsArray.forEach((question) => {
		expectedTotalSurveyTime.value += question.question_time ? question.question_time : 0;
		let desc = question.description;
		let subText = question.sub_text;
		if(survey.value?.type === 2) {
			desc = question.description_past ?? question.description;
			subText = question.sub_text_past ?? question.sub_text;
		}

		const questionObj = {
			id: question.id,
			type: question.question_type,
			answer: question.answer ? question.answer : [],
			timeTaken: question.time_taken ? question.time_taken : 0,
			is_skipped: question.is_skipped ? question.is_skipped : 0,
			is_answered: question.is_answered ? question.is_answered : 0,
			data: {
				meta: {
					question: desc,
					subText: subText,
					icon: question.icon_class,
					inputName: 'input_' + question.id
				},
				mode: question.mode,
				valMin: question.min_value,
				valMax: question.max_value,
				variable_name: question.variable_name,
				max_selections: question.num_choices,
				skippable: question.skippable,
				exclude_values: question.exclude_values,
				question_time: question.question_time,
				question_condition: question.question_condition,
				units: question.units ? question.units.split(',') : [],
				options: [],
				dependents: question.dependents,
				model: null
			}
		};

		question.choices.forEach((choice) => {
			if(choice.type === 'model') {
				questionObj.data.model = choice.value;
			}
			else {
				questionObj.data.options.push({id: 'choice_' + choice.id, text: choice.text, value: choice.value, type: choice.type});
			}
		});

		questions.value.push(questionObj);
	});
}

const onAnswered = (answer) => {
	let normalized = answer;
	if(normalized === null || normalized === undefined) {
		normalized = [];
	}
	if(!Array.isArray(normalized)) {
		normalized = [normalized];
	}

	questions.value[currentQuestionIndex.value].timeTaken += seconds.value;
	questions.value[currentQuestionIndex.value].answer = normalized;
	const varName = questions.value[currentQuestionIndex.value].data.variable_name;
	if(varName) {
		questionVariablesValues.value[varName] = normalized;
	}
	if(varName === 'early_side_effects' || varName === 'other_side_effects' || varName === 'current_side_effects') {
		questionVariablesValues.value['all_side_effects'] = [
			...(questionVariablesValues.value['current_side_effects'] || []),
			...(questionVariablesValues.value['early_side_effects'] || []),
			...(questionVariablesValues.value['other_side_effects'] || [])
		];
	}
	else if(varName === 'early_symptoms' || varName === 'other_symptoms' || varName === 'current_symptoms') {
		questionVariablesValues.value['all_symptoms'] = [
			...(questionVariablesValues.value['current_symptoms'] || []),
			...(questionVariablesValues.value['early_symptoms'] || []),
			...(questionVariablesValues.value['other_symptoms'] || [])
		];
	}
}

// Component mapping
const componentType = computed(() => {
	const questionType = currentQuestion.value?.type;
	const componentMapping = {
		CheckboxChoice, LikertScale, RadioChoice, SliderInput, BoxChoices, DropdownSelect, MonthPicker, DatePicker
	};
	return componentMapping[questionType] || null;
});

// Compute current question data with dynamic Likert scales
const currentQuestionData = computed(() => {
	const q = currentQuestion.value;
	if(!q) {
		return null;
	}
	const data = {...q.data};
	if(q.type === 'LikertScale') {
		let varName, selected;
		const base = {minLabel: data.valMin || 'Mild', maxLabel: data.valMax || 'Severe', type: data.mode || 'severity'};
		const repetitive = hasStarredPlaceholder(data?.meta?.question) || hasStarredPlaceholder(data?.meta?.subText);
		console.log('currentQuestionData',data?.meta?.question, hasStarredPlaceholder(data?.meta?.question));
		console.log('currentQuestionData',data?.meta?.subText, hasStarredPlaceholder(data?.meta?.subText));
		if(repetitive){
			varName = getStarredPlaceholderName(data?.meta?.question);
			if(!varName){
				varName = getStarredPlaceholderName(data?.meta?.subText);
			}
			selected = varName ? questionVariablesValues.value[varName] : null;
		}
		console.log('currentQuestionData',selected, varName, questionVariablesValues.value[varName]);

		let scales = [];
		if(repetitive && Array.isArray(selected) && selected.length) {
			scales = selected.map((d, idx) => ({label: d.text || d.value || '???', id: d.value || String(idx + 1), ...base}));
		}
		else {
			scales = [{label: '', id: '1', ...base}];
		}
		data.scales = scales;
	}
	return data;
});

// Conditions logic
const normalize = (value) => {
	if(value == null) {
		return "";
	}
	const s = typeof value === "string" ? value : String(value);
	return s.trim().toLowerCase();
}

const normalizeAnswer = (ans) => {
	if(ans != null && typeof ans === "object" && !Array.isArray(ans)) {
		return new Set([normalize(ans.value), normalize(ans.text), normalize(ans.id)].filter(Boolean));
	}
	return new Set([normalize(ans)]);
}

const dependentIsSatisfied = (dependent) => {
	const dependentQuestion = questions.value.find(q => q.id === dependent.question_id);
	if(!dependentQuestion) {
		return false;
	}
	let dependentAnswers = Array.isArray(dependentQuestion.answer) ? dependentQuestion.answer : [];
	if(dependentAnswers.length === 0) {
		return false;
	}
	const correctAnswer = new Set([normalize(dependent.value), normalize(dependent.text), normalize(dependent.id)].filter(Boolean));
	if(correctAnswer.size === 0) {
		return false;
	}
	for(const answer of dependentAnswers) {
		const normalizedAnswer = normalizeAnswer(answer);
		for(const ca of correctAnswer) {
			if(normalizedAnswer.has(ca)) {
				return true;
			}
		}
	}
	return false;
}

const validateQuestionVariables = (question) => {
	const text = (question?.data?.meta?.question || "") + " " + (question?.data?.meta?.subText || "");
	const regex = /\[(\*?)([a-zA-Z0-9_ ]+?)(\*?)\]/g;
	let match;
	while((match = regex.exec(text)) !== null) {
		const variable = match[1].trim();
		if(validVariables.value.includes(variable)) {
			if(!Object.prototype.hasOwnProperty.call(questionVariablesValues.value, variable) || !questionVariablesValues.value[variable]) {
				return false;
			}
		}
	}
	return true;
}

const hasQuestionConditionsMet = (question) => {
	console.log('hasQuestionConditionsMet', question);
	const dependents = question.data.dependents;
	if(!dependents || dependents.length === 0) {
		console.log('hasQuestionConditionsMet', 'yes', 'no dependents');
		return true;
	}
	const validVariablesMet = validateQuestionVariables(question);
	if(!validVariablesMet) {
		console.log('hasQuestionConditionsMet', 'no', 'invalid variables');
		return false;
	}
	const condition = (question.data?.question_condition ?? "ANY").toUpperCase();
	const results = dependents.map(dependent => dependentIsSatisfied(dependent));
	console.log('hasQuestionConditionsMet', condition === "ALL" ? results.every(Boolean) : results.some(Boolean), 'finally');
	return condition === "ALL" ? results.every(Boolean) : results.some(Boolean);
}

// Navigation helpers that respect conditions
const nextVisibleIndex = (from) => {
	for(let i = from + 1; i < questions.value.length; i++) {
		if(hasQuestionConditionsMet(questions.value[i])) {
			return i;
		}
	}
	return -1;
}

const prevVisibleIndex = (from) => {
	for(let i = from - 1; i >= 0; i--) {
		if(hasQuestionConditionsMet(questions.value[i])) {
			return i;
		}
	}
	return -1;
}

const hasNext = computed(() => nextVisibleIndex(currentQuestionIndex.value) !== -1);
const hasPrev = computed(() => prevVisibleIndex(currentQuestionIndex.value) !== -1);
const canProceed = computed(() => {
	const q = currentQuestion.value;
	if(!q) {
		return false;
	}
	if(q.data?.skippable) {
		return true;
	}
	return Array.isArray(q.answer) && q.answer.length > 0;
});

const onBack = () => {
	const prev = prevVisibleIndex(currentQuestionIndex.value);
	if(prev !== -1) {
		start();
		currentQuestionIndex.value = prev;
		currentQuestion.value = questions.value[currentQuestionIndex.value];
		transitionName.value = 'slide-prev';
	}
}

const onForward = () => {
	const idx = currentQuestionIndex.value;

	if(questions.value[idx].data.skippable && (!questions.value[idx].answer || questions.value[idx].answer.length === 0)) {
		questions.value[idx].is_skipped = 1;
		questions.value[idx].is_answered = 0;
	}
	else {
		questions.value[idx].is_answered = 1;
		questions.value[idx].is_skipped = 0;
	}
	const next = nextVisibleIndex(idx);
	if(next !== -1) {
		start();
		currentQuestionIndex.value = next;
		currentQuestion.value = questions.value[currentQuestionIndex.value];
		transitionName.value = 'slide-continue';
	}
}

// Progress based solely on currentQuestionIndex (irrespective of visibility)
const progressPercent = computed(() => {
	const total = questions.value.length;
	if(total === 0) {
		return 0;
	}
	const idx = Math.max(0, Math.min(currentQuestionIndex.value + 1, total));
	return Math.min(100, Math.max(0, (idx / total) * 100));
});

const setCurrentToFirstVisible = () => {
	const first = questions.value.findIndex(q => hasQuestionConditionsMet(q));
	currentQuestionIndex.value = first === -1 ? 0 : first;
	currentQuestion.value = questions.value[currentQuestionIndex.value] || null;
}

const markUnacceptedQuestionsEmpty = () => {
	questions.value.forEach(question => {
		if(!hasQuestionConditionsMet(question)) {
			question.answer = [];
			const varName = question.data.variable_name;
			if(varName && questionVariablesValues.value[varName]) {
				delete questionVariablesValues.value[varName];
			}
		}
	});
}

const assignValuesToVariablesOnLoad = () => {
	questions.value.forEach(question => {
		if(question.data.variable_name && question.answer && question.answer.length > 0) {
			questionVariablesValues.value[question.data.variable_name] = question.answer;
			if(question.data.variable_name === 'early_side_effects' || question.data.variable_name === 'other_side_effects' || question.data.variable_name === 'current_side_effects') {
				questionVariablesValues.value['all_side_effects'] = [
					...(questionVariablesValues.value['current_side_effects'] || []),
					...(questionVariablesValues.value['early_side_effects'] || []),
					...(questionVariablesValues.value['other_side_effects'] || [])
				];
			}
			else if(question.data.variable_name === 'early_symptoms' || question.data.variable_name === 'other_symptoms' || question.data.variable_name === 'current_symptoms') {
				questionVariablesValues.value['all_symptoms'] = [
					...(questionVariablesValues.value['current_symptoms'] || []),
					...(questionVariablesValues.value['early_symptoms'] || []),
					...(questionVariablesValues.value['other_symptoms'] || [])
				];
			}
		}
	});
}

const submitSurvey = async() => {
	helper.loaderOn(surveyQuestionsArea.value, false, false);
	try {
		markUnacceptedQuestionsEmpty();
		const next = nextVisibleIndex(currentQuestionIndex.value);
		// if survey is anonymous then isLastVisible will be true
		let isLastVisible = next === -1;
		if(props.type === 'anonymous') {
			isLastVisible = true;
		}
		const response = await apiClient.post('member/surveys/submitSurvey', {
			surveyId: surveyId.value,
			lastQuestionId: currentQuestionIndex.value,
			isCompleted: isLastVisible,
			answers: questions.value.map((q) => ({
				question_id: q.id,
				variable_name: q.data.variable_name,
				answer: q.answer,
				time_taken: q.timeTaken,
				is_skipped: q.is_skipped,
				is_answered: q.is_answered
			})),
			skippedCount: questions.value.filter(q => q.is_skipped === 1).length,
			answeredCount: questions.value.filter(q => q.is_answered === 1).length
		});
		if(response.status === 200) {
			const heading = isLastVisible ? 'Survey Submitted' : 'Survey Saved';
			const message = isLastVisible ? 'Thank you for completing the survey!' : 'Your survey response has been saved, you can return to complete it at any time.';
			toast.displayToast({heading, message}, 'outline-success', 8000);
			if(auth.isLoggedIn && props.type === 'identified') {
				await router.push({path: '/profile'});
			}
			else {
				await router.push({path: '/'});
			}
		}
	}
	catch(error) {
		await useHttpErrorManager().handleError(error, true);
	}
	finally {
		helper.loaderOff(surveyQuestionsArea.value);
	}
}

const removeSurvey = async() => {
	if(!surveyId.value || !auth.isLoggedIn) {
		await router.push({path: '/'});
		return true;
	}
	helper.loaderOn(surveyQuestionsArea.value, false, false);
	try {
		const response = await apiClient.post('member/surveys/removeSurvey', {surveyId: surveyId.value});
		if(response.status === 200) {
			await router.push({path: '/profile'});
			return true;
		}
	}
	catch(error) {
		await useHttpErrorManager().handleError(error, true);
	}
	finally {
		helper.loaderOff(surveyQuestionsArea.value);
	}
}

// Guards and route-related watches
watch(() => props.type, (newVal, oldVal) => {
	if(newVal !== 'anonymous' && newVal !== 'identified') {
		surveyFound.value = false;
		return;
	}
	if(newVal === 'identified' && !auth.isLoggedIn) {
		router.push({path: '/'});
	}
}, {immediate: true});

watch(surveyId, (newVal, oldVal) => {
	if(newVal !== oldVal && newVal && !auth.isLoggedIn) {
		router.push({path: '/survey/anonymous'});
	}
}, {immediate: true});

// Lifecycle
onMounted(() => {
	document.documentElement.classList.add('hide-scroll');
});
onUnmounted(() => {
	document.documentElement.classList.remove('hide-scroll');
});
</script>

<style lang="scss" scoped>
.mw-500 {
	max-width: 500px;
}

.pb-lg {
	padding-bottom: 6em;
}

button {
	&:disabled {
		cursor: not-allowed;
		pointer-events: all !important;
	}
}

.fixed-bottom {
	padding-right: calc(var(--bs-gutter-x) * 0.5);
	padding-left: calc(var(--bs-gutter-x) * 0.5);
}

.lg-sv-btn {
	height: 2.8em;
	width: 9em;
}

.bg-hover-gray {
	&:hover {
		background-color: #f5f5f7;
	}
}

@media (min-width: 992px) {
	.pb-lg-2r {
		padding-bottom: 2.5rem !important;
	}
}
</style>
